import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { previewTermoPdfService } from '@/services/api';
import { Estagio, EstagioFilters, PaginationResponse } from '@/types';
import { DocumentMagnifyingGlassIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useErrorHandler } from '@/hooks/useErrorHandler';

// Função utilitária para formatar data sem problema de timezone
const formatarDataUTC = (dataString: string): string => {
  const data = new Date(dataString);
  const dia = String(data.getUTCDate()).padStart(2, '0');
  const mes = String(data.getUTCMonth() + 1).padStart(2, '0');
  const ano = data.getUTCFullYear();
  return `${dia}/${mes}/${ano}`;
};

export const TermoContratoPage: React.FC = () => {
  const [filters, setFilters] = useState<EstagioFilters>({
    page: 1,
    limit: 10,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const { handleError } = useErrorHandler();
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchTerm || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const { data: estagiosData, isLoading, error } = useQuery<PaginationResponse<Estagio>>({
    queryKey: ['estagios-preview-pdf', filters],
    queryFn: () => previewTermoPdfService.listEstagios(filters),
  });

  useEffect(() => {
    if (error) {
      const message = handleError(error as any, 'Erro ao carregar estágios');
      setLoadError(message);
    }
  }, [error, handleError]);

  useEffect(() => {
    if (estagiosData) {
      setLoadError(null);
    }
  }, [estagiosData]);

  const clearFilters = () => {
    setFilters({ page: 1, limit: 10 });
    setSearchTerm('');
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleGerarPreviewPdf = async (estagioId: number) => {
    try {
      toast.loading('Gerando preview em PDF...', { id: 'gerar-preview-pdf' });
      const response = await previewTermoPdfService.gerarPreviewPdf(estagioId);

      // Criar URL do blob e abrir em nova aba
      const url = window.URL.createObjectURL(response);
      window.open(url, '_blank');

      // Limpar URL após um pequeno delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

      toast.success('Preview PDF gerado com sucesso! Abrindo em nova aba...', { id: 'gerar-preview-pdf' });
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      const errorMessage = axiosError?.response?.data?.error || 'Erro ao gerar preview PDF';
      toast.error(errorMessage, { id: 'gerar-preview-pdf' });
    }
  };

  if (loadError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Termo de Contrato </h1>
        <p className="mt-1 text-sm text-gray-500">
          Visualize e imprima termos de compromisso em formato PDF
        </p>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span>Filtros</span>
            <span className="text-sm font-normal text-gray-500">
              ({estagiosData?.pagination.total || 0} estágios encontrados)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar
              </label>
              <input
                type="text"
                placeholder="Aluno, empresa, instituição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={clearFilters}
                className="w-full sm:w-auto sm:px-6"
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estágios List */}
      <Card>
        <CardHeader>
          <CardTitle>Estágios Disponíveis</CardTitle>
          <CardDescription>
            Selecione um estágio para gerar o termo de compromisso em PDF (abre no navegador)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Carregando estágios...</p>
            </div>
          ) : estagiosData?.data.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhum estágio encontrado</p>
            </div>
          ) : (
            <div className="space-y-4">
              {estagiosData?.data.map((estagio: Estagio) => (
                (() => {
                  const possuiAgenteIntegrador = Boolean(estagio.agenteIntegradorId);

                  return (
                <div
                  key={estagio.id}
                  className="border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col gap-4">
                    {/* Informações do Estágio */}
                    <div className="flex-1">
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3">
                        {estagio.aluno?.nome || 'Aluno não encontrado'}
                      </h3>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        <p className="text-sm text-gray-600 truncate">
                          <span className="font-medium">Empresa:</span> {estagio.empresa?.nome || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600 truncate">
                          <span className="font-medium">Instituição:</span> {estagio.instituicao?.nome || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Tipo:</span> {estagio.tipo}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Início:</span> {formatarDataUTC(estagio.dataInicio)}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Término:</span> {formatarDataUTC(estagio.dataTermino)}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Status:</span> {estagio.status}
                        </p>
                        {possuiAgenteIntegrador && (
                          <p className="text-sm text-amber-700 sm:col-span-2 lg:col-span-3">
                            <span className="font-medium">Vinculado ao agente:</span> {estagio.agenteIntegrador?.nome || 'Agente integrador'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Botão de Ação */}
                    <div className="flex justify-end sm:justify-start">
                      <Button
                        onClick={() => handleGerarPreviewPdf(estagio.id)}
                        className={`w-full sm:w-auto ${possuiAgenteIntegrador ? 'bg-gray-300 text-gray-600 cursor-not-allowed hover:bg-gray-300' : 'bg-green-600 hover:bg-green-700'}`}
                        disabled={possuiAgenteIntegrador}
                      >
                        <DocumentMagnifyingGlassIcon className="h-4 w-4 sm:mr-2" />
                        <span className="ml-2 sm:ml-0">{possuiAgenteIntegrador ? 'Contrato bloqueado' : 'Gerar Termo'}</span>
                      </Button>
                    </div>
                  </div>
                </div>
                  );
                })()
              ))}
            </div>
          )}

          {/* Pagination */}
          {estagiosData && estagiosData.pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={!estagiosData.pagination.hasPrev}
                  className="px-3"
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 py-2 text-sm text-gray-700">
                  Página {estagiosData.pagination.page} de {estagiosData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={!estagiosData.pagination.hasNext}
                  className="px-3"
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
